import {
  sequenceStepTypes,
  stepTypeMapping,
  validationTypes,
  mailboxTabTypes,
  validationCategories,
  getValidationCategoryDataKeyMapping,
} from "@/src/constants/sequenceConstants.js";
import SequenceService from "@/src/api/SequenceService.js";
import { featureNames } from "@/src/constants/globalConstants";

export const generatePreview = async (id, payload, type) => {
  try {
    let response;

    switch (type) {
      case sequenceStepTypes.EMAIL:
        response = await SequenceService.generatePreviewEmail(id, payload);
        break;
      case sequenceStepTypes.LINKEDIN_MESSAGE:
        response = await SequenceService.generatePreviewLinkedin(id, payload);
        break;
      case sequenceStepTypes.LINKEDIN_INMAIL:
        response = await SequenceService.generatePreviewLinkedin(id, payload);
        break;
      default:
        console.warn(`Unsupported Sequence step type: ${type}`);
        return null;
    }

    return response?.success ? response : null;
  } catch (error) {
    console.error("An error occurred while generating preview:", error);
    return null;
  }
};

export const calculateJourneyDuration = (journeySteps) => {
  if (!journeySteps || journeySteps.length === 0) {
    return 1;
  }

  const relativeDays = journeySteps
    .map((step) => step.relative_day)
    .filter((day) => day && day > 0);

  if (relativeDays.length > 0) {
    return Math.max(...relativeDays);
  }

  let totalDays = 1;

  for (const step of journeySteps) {
    if (step.timing_type === "IMMEDIATE") {
      continue;
    }

    if (step.delay_time && step.delay_time_unit) {
      const delay = step.delay_time;
      const unit = step.delay_time_unit.toLowerCase();

      if (unit === "minutes") {
        totalDays += delay / (24 * 60);
      } else if (unit === "hours") {
        totalDays += delay / 24;
      } else if (unit === "days") {
        totalDays += delay;
      }
    } else {
      totalDays += 1;
    }
  }

  return Math.ceil(totalDays);
};

export const getStepIcon = (step) => {
  const type = step?.step_type?.toLowerCase() || "";

  for (const [key, mapping] of Object.entries(stepTypeMapping)) {
    if (type.includes(key.toLowerCase())) {
      return mapping.icon;
    }
  }

  return stepTypeMapping.TASK.icon;
};

export const getIconClass = (step) => {
  const type = step?.step_type?.toLowerCase() || "";

  for (const [key, mapping] of Object.entries(stepTypeMapping)) {
    if (type.includes(key.toLowerCase())) {
      return mapping.iconClass;
    }
  }

  return stepTypeMapping.TASK.iconClass;
};

export const getStepTitle = (step) => {
  const type = step?.step_type?.toLowerCase() || "";
  const automationType = step?.automation_type?.toLowerCase() || "";

  const getAutomationPrefix = (autoType) => {
    switch (autoType) {
      case "ai":
        return "AI ";
      case "automated":
        return "Automated ";
      default:
        return "Manual ";
    }
  };

  const getLinkedinSubtype = (stepType) => {
    if (stepType.includes("connection") || stepType.includes("invite")) {
      return stepTypeMapping.LINKEDIN.titles.CONNECTION;
    } else if (stepType.includes("message")) {
      return stepTypeMapping.LINKEDIN.titles.MESSAGE;
    } else if (stepType.includes("inmail")) {
      return stepTypeMapping.LINKEDIN.titles.INMAIL;
    }
    return stepTypeMapping.LINKEDIN.titles.DEFAULT;
  };

  for (const [key, mapping] of Object.entries(stepTypeMapping)) {
    if (type.includes(key.toLowerCase())) {
      if (key === "EMAIL") {
        const autoKey =
          automationType === "ai"
            ? "AI"
            : automationType === "automated"
              ? "AUTOMATED"
              : "MANUAL";
        return mapping.titles[autoKey] || mapping.titles.DEFAULT;
      } else if (key === "LINKEDIN") {
        const prefix = getAutomationPrefix(automationType);
        const subtype = getLinkedinSubtype(type);
        return `${prefix}${subtype}`;
      } else {
        return mapping.titles.DEFAULT;
      }
    }
  }

  return step?.step_type || "Unknown Step";
};

export const getStepTiming = (step, index) => {
  if (step?.timing_type === "IMMEDIATE") {
    return "Immediately";
  }

  if (step?.delay_time && step?.delay_time_unit) {
    const delay = step.delay_time;
    const unit = step.delay_time_unit.toLowerCase();
    const pluralize = (word, count) => (count > 1 ? `${word}s` : word);

    const unitMap = {
      minutes: () => `After ${delay} ${pluralize("minute", delay)}`,
      hours: () => `After ${delay} ${pluralize("hour", delay)}`,
      days: () => `After ${delay} ${pluralize("day", delay)}`,
    };

    return unitMap[unit] ? unitMap[unit]() : `After ${delay} ${unit}`;
  }

  return `Day ${index + 1}`;
};

export const getStepSubject = (step) => {
  const firstVariant = step?.variants?.[0];
  if (firstVariant?.subject) {
    return firstVariant.subject;
  }
  return null;
};

export const getStepContent = (step) => {
  const type = step?.step_type?.toLowerCase() || "";
  const firstVariant = step?.variants?.[0];

  const getVariantContent = (variant) => {
    if (variant?.message) {
      return variant.message;
    } else if (variant?.instruction) {
      return `<em>AI Instruction: ${variant.instruction}</em>`;
    }
    return null;
  };

  const getTaskContent = (step) => {
    return step?.task_description || null;
  };

  for (const [key, mapping] of Object.entries(stepTypeMapping)) {
    if (type.includes(key.toLowerCase())) {
      if (key === "EMAIL" || key === "LINKEDIN") {
        const variantContent = getVariantContent(firstVariant);
        return variantContent || mapping.defaultContent;
      } else if (key === "PHONE" || key === "CALL" || key === "TASK") {
        const taskContent = getTaskContent(step);
        return taskContent || mapping.defaultContent;
      } else {
        return mapping.defaultContent;
      }
    }
  }

  return "Step details - configuration based on step type.";
};

export const retryWithBackoff = async (
  operation,
  {
    maxRetries = 3,
    initialDelay = 1000,
    backoffMultiplier = 1.5,
    condition = (result) => result === 0,
  } = {},
) => {
  let result = await operation();

  for (let attempt = 1; attempt <= maxRetries && condition(result); attempt++) {
    const delay = Math.round(
      initialDelay * Math.pow(backoffMultiplier, attempt - 1),
    );
    await new Promise((resolve) => setTimeout(resolve, delay));
    result = await operation();
  }

  return result;
};

export const fetchPeopleCountWithRetry = async (
  sequenceId,
  forceRefresh = false,
  retryOptions = {},
) => {
  const operation = async () => {
    try {
      const timestamp = Date.now();
      const data = await SequenceService.fetchSequenceEntityViews(
        sequenceId,
        forceRefresh ? { _t: timestamp } : undefined,
      );

      if (data?.success) {
        const viewId = data.currentView?.id;

        if (viewId) {
          const contactsData =
            await SequenceService.fetchFilteredContactsAndLeads({
              viewId: viewId,
              sequenceId: sequenceId,
              page: 0,
              pageSize: 1,
              searchQuery: "",
              ...(forceRefresh ? { _t: timestamp } : {}),
            });

          if (contactsData?.success) {
            return contactsData?.total || 0;
          }
        }
      }
      return 0;
    } catch (error) {
      console.error("Error fetching people count:", error);
      return 0;
    }
  };

  return await retryWithBackoff(operation, {
    maxRetries: 2,
    initialDelay: 2000,
    backoffMultiplier: 1.5,
    condition: (result) => result === 0,
    ...retryOptions,
  });
};

export const createListPayload = ({
  selectedCategories,
  validationResults,
  sequenceId,
  configId,
  viewId = null,
}) => {
  const payload = {
    id: validationResults?.list_id,
    sequence_id: sequenceId,
    sequence_entity_config_id: configId,
    include_complete_entities: selectedCategories.complete,
    include_empty_email: selectedCategories.empty_email,
    include_empty_linkedin: selectedCategories.empty_linkedin,
    include_completed_sequences: selectedCategories.completed_sequences,
    include_ongoing_sequences: selectedCategories.ongoing_sequences,
    include_existing_sequences: selectedCategories.existing_sequences,
    include_multiple_invalid: selectedCategories.multiple_invalid,
  };
  
  // Add view_id if provided
  if (viewId) {
    payload.view_id = viewId;
  }
  
  return payload;
};

export const createManualPayload = ({
  selectedCategories,
  validationResults,
  configId,
}) => {
  let entityData = [];
  const dataKeyMapping = getValidationCategoryDataKeyMapping();

  Object.entries(dataKeyMapping).forEach(([categoryKey, resultKey]) => {
    if (selectedCategories[categoryKey] && validationResults?.[resultKey]) {
      const entities = validationResults[resultKey].map((entity) => ({
        id: entity.id,
        entity_type: entity.entity_type,
      }));
      entityData = [...entityData, ...entities];
    }
  });

  return {
    sequence_entity_config_id: configId,
    entity_data: entityData,
  };
};

export const createEmailPayload = ({
  selectedCategories,
  validationResults,
  configId,
}) => {
  let entityData = [];
  const dataKeyMapping = getValidationCategoryDataKeyMapping();

  if (selectedCategories.complete && validationResults?.complete_entities) {
    const completeEntities = validationResults.complete_entities.map(
      (entity) => ({
        id: entity.id,
        entity_type: entity.entity_type,
      }),
    );
    entityData = [...entityData, ...completeEntities];
  }

  Object.entries(selectedCategories).forEach(([categoryKey, isSelected]) => {
    if (isSelected && categoryKey !== "complete") {
      const dataKey = dataKeyMapping[categoryKey];
      if (validationResults?.[dataKey]) {
        const categoryEntities = validationResults[dataKey].map((entity) => ({
          id: entity.id,
          entity_type: entity.entity_type,
        }));
        entityData = [...entityData, ...categoryEntities];
      }
    }
  });

  return {
    sequence_entity_config_id: configId,
    entity_data: entityData,
  };
};

export const createValidationPayload = (validationType, data) => {
  const basePayload = {
    sequence_id: data.sequenceId,
    page_size: validationType === validationTypes.LIST ? 5 : 500,
    empty_linkedin_page: 1,
    empty_email_page: 1,
    completed_sequences_page: 1,
    ongoing_sequences_page: 1,
    complete_entities_page: 1,
    existing_sequences_page: 1,
    multiple_invalid_page: 1,
    user_id: localStorage.getItem("userId"),
  };

  if (validationType === validationTypes.EMAIL) {
    return {
      ...basePayload,
      validation_type: validationType,
      entity_emails: data.entityEmails,
      ...(data.filterData || {}), // Spread filters directly instead of nesting
    };
  } else if (validationType === validationTypes.LIST) {
    const payload = {
      ...basePayload,
      validation_type: validationType,
      list_id: data.listId,
    };
    
    // Add view_id if provided
    if (data.viewId) {
      payload.view_id = data.viewId;
    }
    
    return payload;
  } else {
    return {
      ...basePayload,
      validation_type: validationType,
      entity_list: data.entityList,
    };
  }
};

export const fetchApiData = async (
  apiCall,
  dataKey,
  targetRef,
  loadingRef,
  transformer,
) => {
  try {
    loadingRef.value = true;
    const response = await apiCall();

    if (response?.success && response?.[dataKey]) {
      targetRef.value = response[dataKey].map(transformer);
    } else {
      console.error(
        `Failed to fetch ${dataKey}:`,
        response?.errors || response,
      );
      targetRef.value = [];
    }
  } catch (error) {
    console.error(`Error fetching ${dataKey}:`, error);
    targetRef.value = [];
  } finally {
    loadingRef.value = false;
  }
};

export const extractItemValue = (item) => {
  if (typeof item === "object" && item !== null) {
    return item.value || item.id || item;
  }
  return item;
};

export const getSelectedMailboxIds = (
  localMailboxRotation,
  userMailboxes,
  mailboxSelection,
) => {
  if (!userMailboxes?.length) return [];

  if (
    !mailboxSelection ||
    (!mailboxSelection.select_all &&
      (!mailboxSelection.selected_ids ||
        mailboxSelection.selected_ids.length === 0))
  ) {
    return [];
  }

  if (mailboxSelection.select_all) {
    return userMailboxes
      .filter(
        (mailbox) =>
          !mailboxSelection.unselected_ids.includes(mailbox.email_account_id),
      )
      .map((mailbox) => mailbox.email_account_id);
  }

  return mailboxSelection.selected_ids || [];
};

// Build config data based on selection for sequence entity configuration
export const buildSequenceConfigData = (
  selectedItems,
  activeTab,
  options = {},
) => {
  const {
    localMailboxRotation = false,
    userMailboxes = [],
    mailboxSelection = {},
    linkedInIntegrations = [],
    linkedInSelection = {},
    userId = localStorage.getItem("userId"),
  } = options;

  const configData = {
    enable_mailbox_rotation: localMailboxRotation,
    user_id: userId,
    user_ids: [],
    team_id: null,
    email_account_ids: [],
    linkedin_integration_ids: [],
  };

  if (activeTab?.name === mailboxTabTypes.USERS && selectedItems.length > 0) {
    configData.user_ids = selectedItems.map(extractItemValue);
    configData.email_account_ids = getSelectedMailboxIds(
      localMailboxRotation,
      userMailboxes,
      mailboxSelection,
    );
    configData.linkedin_integration_ids = getSelectedLinkedInIntegrationIds(
      linkedInIntegrations,
      linkedInSelection,
    );
  } else if (
    activeTab?.name === mailboxTabTypes.TEAMS &&
    selectedItems.length > 0
  ) {
    configData.team_id = extractItemValue(selectedItems[0]);
    configData.email_account_ids = getSelectedMailboxIds(
      localMailboxRotation,
      userMailboxes,
      mailboxSelection,
    );
    configData.linkedin_integration_ids = getSelectedLinkedInIntegrationIds(
      linkedInIntegrations,
      linkedInSelection,
    );
  }

  return configData;
};

// Get selected LinkedIn integration IDs
export const getSelectedLinkedInIntegrationIds = (
  linkedInIntegrations,
  linkedInSelection,
) => {
  if (!linkedInSelection.selected_ids || linkedInSelection.selected_ids.length === 0) {
    return [];
  }

  return linkedInSelection.selected_ids || [];
};

// Format entities for CustomTable - add display_name field with name/email fallback
export const formatEntitiesForTable = (entities) => {
  return (
    entities?.map((entity) => {
      const fullName =
        `${entity.first_name || ""} ${entity.last_name || ""}`.trim();
      const displayName = fullName || entity.email || "";

      return {
        ...entity,
        name: fullName,
        display_name: displayName,
      };
    }) || []
  );
};

// Get paginated data for display
export const getPaginatedEntities = (entities, paginationState) => {
  const formattedEntities = formatEntitiesForTable(entities);

  if (paginationState?.current && paginationState?.pageSize) {
    const startIndex = (paginationState.current - 1) * paginationState.pageSize;
    const endIndex = startIndex + paginationState.pageSize;
    return formattedEntities.slice(startIndex, endIndex);
  }

  return formattedEntities;
};

// Get entity count based on mode (manual vs list)
export const getEntityCount = (validationResults, category, isManualMode) => {
  if (!validationResults) return 0;

  return validationResults?.totals?.[category.dataKey] || 0;
};

// Group validation categories by their group type
export const groupValidationCategories = () => {
  const groups = {};

  Object.values(validationCategories).forEach((category) => {
    if (!groups[category.group]) {
      groups[category.group] = [];
    }
    groups[category.group].push(category);
  });

  return groups;
};

export const getTotalCategoriesCount = (validationResults, isManualMode) => {
  if (!validationResults) return 0;

  return Object.values(validationCategories).reduce((count, category) => {
    count += getEntityCount(validationResults, category, isManualMode);
    return count;
  }, 0);
};

export const getGroupCount = (validationResults, groupKey, isManualMode) => {
  if (!validationResults) return 0;

  const groupCategories = Object.values(validationCategories).filter(
    (category) => category.group === groupKey,
  );

  return groupCategories.reduce((count, category) => {
    count += getEntityCount(validationResults, category, isManualMode);
    return count;
  }, 0);
};

export const getValidationTableHeaders = (category) => {
  const basePersonColumn = {
    label: "People",
    key: "display_name",
    width: 4,
    avatar: "profile_pic",
    view: true,
    clickable: true,
    redirectTo: (row) => {
      if (row.entity_type === "contact") {
        return { name: "contact-detail", params: { id: row.id } };
      } else if (row.entity_type === "lead") {
        return { name: "lead-detail", params: { id: row.id } };
      }
      return null;
    },
  };

  switch (category.key) {
    case "ongoing_sequences":
    case "completed_sequences":
    case "existing_sequences":
      return [
        basePersonColumn,
        {
          label: featureNames.SEQUENCES,
          key: "sequence_name",
          width: 4,
          view: true,
        },
        {
          label: "Status",
          key: "sequence_data",
          width: 4,
          view: true,
        },
      ];

    case "empty_email":
      return [
        basePersonColumn,
        {
          label: "Email",
          key: "email",
          width: 5,
          view: true,
        },
      ];

    case "empty_linkedin":
      return [
        basePersonColumn,
        {
          label: "LinkedIn",
          key: "linkedin_url",
          width: 5,
          view: true,
        },
      ];

    case "complete":
      return [
        basePersonColumn,
        {
          label: "Role",
          key: "current_role",
          width: 4,
          view: true,
        },
        {
          label: "Company",
          key: "current_company",
          width: 4,
          avatar: "company_logo",
          view: true,
        },
      ];

    case "multiple_invalid":
      return [
        basePersonColumn,
        {
          label: "Issue",
          key: "categories",
          width: 8,
          view: true,
        },
      ];

    default:
      return [basePersonColumn];
  }
};

// Common reusable custom column render function for validation tables
export const getValidationCustomColumnRender = (categoryKey, options = {}) => {
  const {
    EmptyFieldDisplay,
    SequenceDisplayCell,
    MultipleIssuesRenderer,
    onSequenceSelected,
    validationResults,
    currentPerson,
  } = options;

  // Handle multiple invalid case
  if (categoryKey === "multiple_invalid" && MultipleIssuesRenderer) {
    return {
      categories: {
        component: MultipleIssuesRenderer,
        props: (row) => ({
          issueCategories: row.categories || [],
          entityData: validationResults,
          currentPerson: row,
        }),
        listeners: {
          "sequence-selected": (updatedRow) => onSequenceSelected?.(updatedRow),
        },
      },
    };
  }

  // Handle empty email case
  if (categoryKey === "empty_email" && EmptyFieldDisplay) {
    return {
      email: {
        component: EmptyFieldDisplay,
        props: (row) => ({
          value: row.email,
          placeholder: "No email",
          row: row,
        }),
      },
    };
  }

  // Handle empty LinkedIn case
  if (categoryKey === "empty_linkedin" && EmptyFieldDisplay) {
    return {
      linkedin_url: {
        component: EmptyFieldDisplay,
        props: (row) => ({
          value: row.linkedin_url,
          placeholder: "No LinkedIn",
          row: row,
        }),
      },
    };
  }

  // Handle sequence-related cases
  if (
    ["ongoing_sequences", "completed_sequences", "existing_sequences"].includes(
      categoryKey,
    ) &&
    SequenceDisplayCell
  ) {
    return {
      sequence_name: {
        component: SequenceDisplayCell,
        props: (row) => ({
          sequences: row.sequence_data || [],
          displayType: "sequences",
          row: row,
        }),
        listeners: {
          "sequence-selected": (updatedRow) => onSequenceSelected?.(updatedRow),
        },
      },
      sequence_data: {
        component: SequenceDisplayCell,
        props: (row) => ({
          sequences: row.sequence_data || [],
          displayType: "status",
          row: row,
        }),
        listeners: {
          "sequence-selected": (updatedRow) => onSequenceSelected?.(updatedRow),
        },
      },
    };
  }

  // Return empty object for other cases
  return {};
};

export const extractAiVariableIds = (content) => {
  const variableIds = new Set();
  const regex = /\{\{ai_variable\.(\d+)\}\}/g;
  
  const contentArray = Array.isArray(content) ? content : [content];
  
  contentArray.forEach(text => {
    if (text && typeof text === 'string') {
      let match;
      while ((match = regex.exec(text)) !== null) {
        variableIds.add(match[1]);
      }
    }
  });
  
  return variableIds;
};

export const getAllTextNodes = (element) => {
  const textNodes = [];
  
  const traverse = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      textNodes.push(node);
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        traverse(node.childNodes[i]);
      }
    }
  };
  
  traverse(element);
  return textNodes;
};

export const createAiVariableElement = (id, variableName, fullText) => {
  const variableSpan = Object.assign(document.createElement("ai-var"), {
    className:
      "ai_variable_button px-2 py-[3px] mx-1 whitespace-nowrap inline-flex items-center relative text-sm select-all align-middle",
    textContent: variableName,
  });

  [
    ["data-id", id],
    ["data-name", variableName],
    ["data-original", fullText],
    ["contenteditable", "false"],
  ].forEach(([key, value]) => variableSpan.setAttribute(key, value));

  return variableSpan;
};

export const processAiVariableContent = (content, getVariableName) => {
  if (!content || !content.includes("{{ai_variable.")) {
    return content;
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = content;

  const textNodes = getAllTextNodes(tempDiv);

  for (const node of textNodes) {
    if (!node.textContent) continue;

    let match;
    const matches = [];

    const nodeText = node.textContent;
    const regex = /{{ai_variable\.(\d+)}}/g;

    while ((match = regex.exec(nodeText)) !== null) {
      matches.push({
        id: match[1],
        index: match.index,
        fullText: match[0],
        length: match[0].length,
      });
    }

    if (matches.length === 0) continue;

    matches.sort((a, b) => b.index - a.index);

    for (const match of matches) {
      const { id, fullText, index, length } = match;
      const variableName = getVariableName(id);

      const variableSpan = createAiVariableElement(id, variableName, fullText);

      const range = document.createRange();
      range.setStart(node, index);
      range.setEnd(node, index + length);
      range.deleteContents();
      range.insertNode(variableSpan);
    }
  }

  return tempDiv.innerHTML;
};
